import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Lock, StickyNote, Mic, MicOff } from 'lucide-react';

/**
 * StickyNoteModal component for creating and editing sticky notes
 * The first line of the note becomes the headline, remaining lines are the content.
 *
 * @param {Object} props
 * @param {Object} [props.note] - Existing note for editing, or null for create
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSave - Save handler (receives note data)
 * @param {Object} [props.defaultPosition] - Position object for new notes (from drag-to-place)
 * @param {string} [props.defaultComponentId] - Component ID to place new notes inside (fallback)
 */
const StickyNoteModal = ({
  note,
  onClose,
  onSave,
  defaultPosition,
  defaultComponentId
}) => {
  const isEdit = !!note;

  // For editing, combine headline and content into a single text
  const getInitialText = () => {
    if (!note) return '';
    if (note.content) {
      return `${note.headline}\n${note.content}`;
    }
    return note.headline || '';
  };

  const [formData, setFormData] = useState({
    noteText: getInitialText(),
    is_confidential: note?.is_confidential || false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Speech-to-text state using Web Speech API
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const recognitionRef = useRef(null);
  const textBeforeSpeechRef = useRef('');

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Update the text in real-time
      const baseText = textBeforeSpeechRef.current;
      const separator = baseText && !baseText.endsWith('\n') ? '\n' : '';
      const currentTranscript = finalTranscript || interimTranscript;

      setFormData(prev => ({
        ...prev,
        noteText: baseText + separator + currentTranscript
      }));
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        setError('Microphone access denied. Please allow microphone access and try again.');
      } else if (event.error === 'no-speech') {
        // This is normal, just means silence was detected
      } else {
        setError(`Speech recognition error: ${event.error}`);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      // Only update state if we were actively listening (not a manual stop)
      if (recognitionRef.current?.manualStop) {
        recognitionRef.current.manualStop = false;
      }
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const startListening = useCallback(() => {
    if (!recognitionRef.current) return;

    setError('');
    // Store the current text before starting speech
    textBeforeSpeechRef.current = formData.noteText;

    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch (err) {
      console.error('Error starting speech recognition:', err);
      setError('Failed to start speech recognition. Please try again.');
    }
  }, [formData.noteText]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;

    recognitionRef.current.manualStop = true;
    recognitionRef.current.stop();
    setIsListening(false);
  }, []);

  // Parse the note text into headline (first line) and content (rest)
  const parseNoteText = (text) => {
    const trimmed = text.trim();
    const lines = trimmed.split('\n');
    const headline = lines[0]?.trim() || '';
    const content = lines.slice(1).join('\n').trim();
    return { headline, content };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Stop listening if still active
    if (isListening) {
      stopListening();
    }

    try {
      const { headline, content } = parseNoteText(formData.noteText);

      if (!headline) {
        setError('Please enter a note. The first line will be used as the headline.');
        setLoading(false);
        return;
      }

      // For new notes, use the provided position in order of priority:
      // 1. defaultPosition (from drag-to-place)
      // 2. defaultComponentId (topmost visible component)
      // 3. End of timeline
      let position = note?.position;
      if (!position) {
        if (defaultPosition) {
          position = defaultPosition;
        } else if (defaultComponentId) {
          position = {
            position_type: 'inside',
            attached_to_component_id: defaultComponentId,
            rank: 0,
            offset_x: 5,
            offset_y: 5
          };
        } else {
          position = {
            position_type: 'end',
            rank: 0
          };
        }
      }

      const noteData = {
        headline,
        content,
        is_confidential: formData.is_confidential,
        position
      };

      await onSave(noteData, note?.id);
    } catch (err) {
      console.error('Sticky note operation error:', err);
      setError(`Failed to ${isEdit ? 'update' : 'create'} note: ` + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  };

  // Determine placement message for new notes
  const getPlacementMessage = () => {
    if (isEdit) return null;
    if (defaultPosition) {
      if (defaultPosition.position_type === 'inside') {
        return 'Note will be placed at your selected location.';
      }
      return 'Note will be added to the timeline.';
    }
    if (defaultComponentId) {
      return 'Note will be added inside the topmost visible component.';
    }
    return 'Tip: After creating, drag the note to position it on the timeline.';
  };

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-sm shadow-lg rounded-md bg-ui-surface">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-ui-amber-fg" />
              <h3 className="text-lg font-medium text-ui-ink">
                {isEdit ? 'Edit Note' : 'Add Note'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-ui-muted hover:text-ui-ink-3 text-xl"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-ui-red-bg border border-ui-red-line rounded-md">
              <p className="text-sm text-ui-red-fg">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Note content - first line becomes headline */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-ui-ink-2">
                  Note <span className="text-ui-red-fg">*</span>
                </label>
                {/* Speech-to-text button */}
                {speechSupported && (
                  <button
                    type="button"
                    onClick={isListening ? stopListening : startListening}
                    disabled={loading}
                    className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                      isListening
                        ? 'bg-ui-red-bg text-ui-red-fg hover:bg-ui-red-line animate-pulse'
                        : 'bg-ui-amber-bg text-ui-amber-fg hover:bg-ui-amber-line'
                    }`}
                    title={isListening ? 'Stop listening' : 'Start voice input'}
                  >
                    {isListening ? (
                      <>
                        <MicOff className="w-3.5 h-3.5" />
                        <span>Stop</span>
                      </>
                    ) : (
                      <>
                        <Mic className="w-3.5 h-3.5" />
                        <span>Voice</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              <textarea
                value={formData.noteText}
                onChange={(e) => setFormData(prev => ({ ...prev, noteText: e.target.value }))}
                className={`w-full border rounded-md px-3 py-2 text-sm ${
                  isListening ? 'border-ui-red-line bg-ui-red-bg' : 'border-ui-control'
                }`}
                rows="4"
                placeholder="First line is the headline...&#10;Additional details go here..."
                required
                autoFocus
              />
              <p className="mt-1 text-xs text-ui-muted">
                {isListening ? 'Listening... Speak now' : 'First line becomes the headline'}
              </p>
            </div>

            {/* Confidential toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_confidential"
                checked={formData.is_confidential}
                onChange={(e) => setFormData(prev => ({ ...prev, is_confidential: e.target.checked }))}
                className="h-4 w-4 text-ui-amber-fg focus:ring-ui-amber-solid border-ui-control rounded"
              />
              <label htmlFor="is_confidential" className="flex items-center gap-2 text-sm text-ui-ink-2">
                <Lock className="w-4 h-4 text-ui-muted" />
                <span>Confidential (only visible to you)</span>
              </label>
            </div>

            {getPlacementMessage() && (
              <p className="text-xs text-ui-muted">
                {getPlacementMessage()}
              </p>
            )}

            {/* Action buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-ui-line">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-ui-ink-2 bg-ui-surface border border-ui-control rounded-md hover:bg-ui-hover"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-ui-on-primary bg-ui-amber-strong rounded-md hover:bg-ui-amber-strong/90 disabled:opacity-50"
                disabled={loading || isListening}
              >
                {loading ? 'Saving...' : isEdit ? 'Save' : 'Add Note'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StickyNoteModal;
